import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, useFormState } from "react-hook-form";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Types.cd w
import IntegrationType from "../../../../types/integration/IntegrationType";

// Constants.
import {
    ACTIONS,
    generalErrorMessage,
    coPilotAttributesArray,
    openAiModels,
    openRouterModels,
    googleModels
} from '../../../../component/Constants';

//Utils
import { findIntegrationValue, getAction, getValidClass } from "../../../../helpers/utils";
import Action from "../../../../types/Action";
import api from "../../../../api/Base";
import Alert from "../../../../component/Alert";
import { getModelPricing, FALLBACK_PRICING, resolveModelPricing, ModelPricing } from "../../../../component/chatGPT/pricing";

function Detail(props: { coPilot: IntegrationType[], refresh: Function, isAdmin: boolean }) {
    const { actionId } = useParams();
    const [action, setAction] = useState<Action>(ACTIONS.CREATE);
    const [apikeyElementType, setApiKeyElementType] = useState<string>('password');
    const [apiKeyError, setApiKeyError] = useState<string | null>(null);
    const [validatingApiKey, setValidatingApiKey] = useState<boolean>(false);

    const navigate = useNavigate();

    // Redirect non-admin users
    useEffect(() => {
        if (!props.isAdmin) {
            navigate('/admin/integrations/co-pilot/');
        }
    }, [props.isAdmin, navigate]);
    const isDelete = action.type === ACTIONS.DELETE.type;
    const {
        register,
        trigger,
        handleSubmit,
        getValues,
        setValue,
        control,
        watch,
        formState: { errors }
    } = useForm<IntegrationType>({
        mode: "onBlur",
        reValidateMode: 'onSubmit',
        shouldFocusError: true
    });

    // Watch the provider field for changes
    const selectedProvider = watch('name');
    const selectedModel = watch('model');

    // Pricing state
    const [pricing, setPricing] = useState<Record<string, any> | null>(null);
    const [sortColumn, setSortColumn] = useState<'model' | 'input' | 'output'>('model');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    useEffect(() => {
        getAction(actionId, setAction, setDisabled);
        // Fetch pricing on mount
        getModelPricing().then(setPricing);
    }, [actionId]);

    // Reset model when provider changes
    useEffect(() => {
        // Always clear API key error when provider changes (including when cleared)
        setApiKeyError(null);

        // If no provider is selected, clear the model and exit
        if (!selectedProvider) {
            setValue('model', '', { shouldDirty: true });
            return;
        }

        // If a model is already set (e.g. when editing an existing integration),
        // don't overwrite it with default values.
        if (selectedModel) {
            return;
        }

        // Apply provider-specific defaults only when there is no current model
        if (selectedProvider === 'OpenAI') {
            setValue('model', 'gpt-4-turbo', { shouldDirty: true });
        } else if (selectedProvider === 'OpenRouter') {
            setValue('model', 'openai/gpt-3.5-turbo', { shouldDirty: true });
        } else if (selectedProvider === 'AzureOpenAI') {
            // For Azure OpenAI, the deployment/model is derived from the Target URI
            // in onSubmit(), so we explicitly keep this empty here.
            setValue('model', '', { shouldDirty: true });
        } else if (selectedProvider === 'Google') {
            setValue('model', 'gemini-2.5-pro', { shouldDirty: true });
        } else if (selectedProvider === 'None') {
            setValue('model', '', { shouldDirty: true });
        }
    }, [selectedProvider, selectedModel, setValue, setApiKeyError]);

    const [disabled, setDisabled] = useState<boolean>(actionId === ACTIONS.DELETE.type);

    // Validate API key before saving
    const validateApiKey = async (provider: string, key: string): Promise<string | null> => {
        try {
            let response;
            if (provider === 'OpenAI') {
                // Get the base URL from the url field, default to standard OpenAI endpoint
                const baseUrl = (getValues('url') || 'https://api.openai.com').toString().trim();
                const apiUrl = baseUrl.endsWith('/') ? `${baseUrl}v1/models` : `${baseUrl}/v1/models`;

                response = await fetch(apiUrl, {
                    headers: { 'Authorization': `Bearer ${key}` }
                });
                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        errorMsg = data?.error?.message || response.statusText;
                    } catch {
                        errorMsg = response.statusText || 'Invalid API key or insufficient permissions.';
                    }
                    return errorMsg;
                }
                return null;
            } else if (provider === 'OpenRouter') {
                // Try /api/v1/auth/key for validation
                response = await fetch('https://openrouter.ai/api/v1/auth/key', {
                    headers: { 'Authorization': `Bearer ${key}` }
                });
                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        errorMsg = data?.error?.message || response.statusText;
                    } catch {
                        errorMsg = response.statusText || 'Invalid OpenRouter API key.';
                    }
                    return errorMsg;
                }
                // Optionally, check for error in body
                let data;
                try {
                    data = await response.json();
                } catch {
                    return 'Invalid response from OpenRouter API.';
                }
                if (data?.error) {
                    return data.error.message || 'Invalid OpenRouter API key.';
                }
                return null;
            } else if (provider === 'AzureOpenAI') {
                // For Azure OpenAI, validate against the configured Target URI using a minimal non-streaming request.
                const targetUri = (getValues('url') || '').toString().trim();
                if (!targetUri) {
                    return 'Please enter a Target URI before validating the Azure OpenAI API key.';
                }

                let url: URL;
                try {
                    url = new URL(targetUri);
                } catch {
                    return 'Invalid Azure OpenAI Target URI.';
                }

                response = await fetch(url.toString(), {
                    method: 'POST',
                    headers: {
                        'api-key': key,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        messages: [{ role: 'user', content: 'test' }],
                        max_tokens: 1
                    })
                });

                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        // Azure returns { error: { message, code, ... } }
                        errorMsg = data?.error?.message || response.statusText || 'Invalid Azure OpenAI API key or Target URI.';
                    } catch {
                        errorMsg = response.statusText || 'Invalid Azure OpenAI API key or Target URI.';
                    }
                    return errorMsg;
                }
                return null;
            } else if (selectedProvider === 'Google') {
                // Validate Google API Key
                response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${key}`);
                if (!response.ok) {
                    let errorMsg = '';
                    try {
                        const data = await response.json();
                        errorMsg = data?.error?.message || response.statusText;
                    } catch {
                        errorMsg = response.statusText || 'Invalid Google API key.';
                    }
                    return errorMsg;
                }
                return null;
            } else {
                return null;
            }
        } catch (e) {
            return 'Network error while validating API key.';
        }
    };

    const parseEditResponse = (data: any) => {
        if (data.length) {
            // @ts-ignore
            coPilotAttributesArray.forEach(attr => setValue(attr, findIntegrationValue(attr, data, 'ai.provider')))
            void trigger()
        }
    }

    useEffect(() => {
        parseEditResponse(props.coPilot)
    }, [props.coPilot]);

    const { dirtyFields } = useFormState({
        control
    })


    const onSubmit = async (data: IntegrationType) => {
        setDisabled(true)
        setApiKeyError(null)

        // For Azure OpenAI, derive the model name from the Target URI so it can be displayed elsewhere (e.g. Co-pilot list)
        if (data.name === 'AzureOpenAI' && data.url) {
            try {
                const urlObj = new URL(data.url);
                const match = urlObj.pathname.match(/\/openai\/deployments\/([^/]+)/);
                if (match && match[1]) {
                    data.model = match[1]; // e.g. "gpt-4o"
                }
            } catch {
                // If parsing fails, leave data.model as-is; do not block saving
            }
        }

        // Validate API key if provider is OpenAI or OpenRouter and not deleting
        if (action.type !== 'delete' && data.name && data.name !== 'None' && data.plainkey) {
            setValidatingApiKey(true)
            const errorMsg = await validateApiKey(data.name, data.plainkey);
            setValidatingApiKey(false)
            if (errorMsg) {
                setApiKeyError(errorMsg);
                setDisabled(false);
                return;
            }
        }

        const updatedValues: { name: string; value: any; }[] = []
        let result: any;
        let url = `/parameter`;

        Object.keys(data).forEach(item => {
            let name = `ai.provider.${item}`
            // @ts-ignore
            const value = data[item] === true ? 'true' : data[item] === false ? 'false' : data[item]
            if (action.type === 'create' || action.type === 'delete') {
                updatedValues.push({ name, value })
                // @ts-ignore
            } else if (dirtyFields[item]) {
                updatedValues.push({ name, value })
            }
        })

        result = action.type === 'delete' ? await api.delete(url, { data: updatedValues }) : await api.post(url, updatedValues)

        if (result.status === 200) {
            navigate('/admin/integrations/co-pilot')
            props.refresh()
        }
    }

    const isValid = Object.keys(errors).length === 0

    return (
        <div className="col">
            <style>
                {`
                        .model-select-table .table th {
                            cursor: pointer;
                            user-select: none;
                        }
                        .model-select-table .table tbody tr {
                            cursor: pointer;
                        }
                        .model-select-table .table tbody tr:hover {
                            background-color: rgba(0,123,255,0.1);
                        }
                        .model-select-table .table tbody tr.selected {
                            background-color: rgba(0,123,255,0.2);
                            font-weight: bold;
                        }
                        .model-select-table {
                            max-height: 300px;
                            overflow-y: auto;
                            border: 1px solid #dee2e6;
                            border-radius: 0.375rem;
                            min-width: 500px;
                        }
                        .model-select-table .table td {
                            padding: 0.5rem;
                            font-size: 0.9rem;
                        }
                        .model-select-table .table th {
                            padding: 0.5rem;
                            font-size: 0.9rem;
                            font-weight: 600;
                        }
                    `}
            </style>
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-microchip-ai me-1" aria-hidden="true" />
                    {action.title} Co-Pilot Integration
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {!isValid && (
                            <Alert message={generalErrorMessage.text} heading={generalErrorMessage.title}
                                variant="alert-danger" />
                        )}

                        <p>Please complete the required details below:</p>

                        {/* AI Provider */}
                        <div className="row g-0 form">
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="ai-provider" className="base-url">AI Provider</label>
                                </span>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-4" style={{ width: '500px' }}>
                                <select
                                    id="ai-name"
                                    {...register('name', {
                                        required: "Please select a provider"
                                    })}
                                    className={`form-control ${getValidClass(errors.name, getValues('name'), isDelete)}`}
                                    defaultValue=""
                                    disabled={disabled}
                                    title={errors.name?.message}
                                    onChange={(e) => {
                                        // setValue('url', '');
                                        setValue('plainkey', '');
                                        setValue('model', '');
                                        register('name').onChange(e);
                                    }}
                                >
                                    <option value="">Select a provider...</option>
                                    <option value="OpenAI">OpenAI</option>
                                    <option value="OpenRouter">OpenRouter</option>
                                    <option value="Google">Google</option>
                                    <option value="AzureOpenAI">Microsoft Azure OpenAI</option>
                                    <option value="None">None</option>
                                </select>
                                <small className="text-danger">{errors.name?.message}</small>
                            </div>
                            <div className="w-100" />

                            {/* Base URL for OpenAI */}
                            {selectedProvider === 'OpenAI' && (
                                <>
                                    <div className="col-xs-12 col-md-3">
                                        <label htmlFor="url" className="base-url">Base URL</label>
                                    </div>

                                    <div className="col-xs-12 col-md-6 col-lg-4">
                                        <input
                                            type="text"
                                            id="url"
                                            maxLength={500}
                                            {...register('url', {
                                                required: false,
                                                validate: (value) => {
                                                    // If empty, it's valid (will use default)
                                                    if (!value || value.trim() === '') {
                                                        return true;
                                                    }
                                                    // Check if it starts with https://
                                                    if (!value.trim().startsWith('https://')) {
                                                        return 'URL must start with https://';
                                                    }
                                                    // Try to parse as URL to ensure it's valid
                                                    try {
                                                        new URL(value.trim());
                                                        return true;
                                                    } catch {
                                                        return 'Please enter a valid URL';
                                                    }
                                                }
                                            })}
                                            className={`form-control ${getValidClass(errors.url, getValues('url'), isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            placeholder="https://api.openai.com"
                                            title={errors.url?.message}
                                            data-lpignore={true}
                                        />
                                        <small className="text-muted">
                                            Optional. Defaults to https://api.openai.com. Use https://us.api.openai.com for US customers.
                                        </small>
                                        <small className="text-danger d-block">{errors.url?.message}</small>
                                    </div>
                                    <div className="w-100" />
                                </>
                            )}

                            {/* Azure Target URI (only for Azure OpenAI) */}
                            {selectedProvider === 'AzureOpenAI' && (
                                <>
                                    <div className="col-xs-12 col-md-3">
                                        <span className="required">
                                            <label htmlFor="url" className="base-url">Target URI</label>
                                        </span>
                                    </div>

                                    <div className="col-xs-12 col-md-6 col-lg-4">
                                        <input
                                            type="text"
                                            id="url"
                                            maxLength={500}
                                            {...register('url', {
                                                required: selectedProvider === 'AzureOpenAI' ? "Please fill in this field" : false,
                                            })}
                                            className={`form-control ${getValidClass(errors.url, getValues('url'), isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            title={errors.url?.message}
                                            data-lpignore={true}
                                        />
                                        <small className="text-muted">
                                            Paste the full Azure chat Target URI from the Azure portal, including the api-version parameter.
                                        </small>
                                        <small className="text-danger d-block">{errors.url?.message}</small>
                                    </div>
                                    <div className="w-100" />
                                </>
                            )}


                            {/* Model */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="ai-model" className="base-url">Model</label>
                                </span>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-4" style={{ width: '500px' }}>
                                <div>
                                    {selectedProvider && selectedProvider !== 'None' ? (
                                        selectedProvider === 'AzureOpenAI' ? (
                                            <div className="text-muted">
                                                Model will be derived automatically from the Azure Target URI.
                                            </div>
                                        ) : (
                                            <div className="model-select-table">
                                                <table className="table table-hover mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th
                                                                onClick={() => {
                                                                    if (sortColumn === 'model') {
                                                                        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                                                                    } else {
                                                                        setSortColumn('model');
                                                                        setSortDirection('asc');
                                                                    }
                                                                }}
                                                            >
                                                                Model {sortColumn === 'model' && (sortDirection === 'asc' ? '▲' : '▼')}
                                                            </th>
                                                            <th
                                                                onClick={() => {
                                                                    if (sortColumn === 'input') {
                                                                        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                                                                    } else {
                                                                        setSortColumn('input');
                                                                        setSortDirection('asc');
                                                                    }
                                                                }}
                                                            >
                                                                Input Cost {sortColumn === 'input' && (sortDirection === 'asc' ? '▲' : '▼')}
                                                            </th>
                                                            <th
                                                                onClick={() => {
                                                                    if (sortColumn === 'output') {
                                                                        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                                                                    } else {
                                                                        setSortColumn('output');
                                                                        setSortDirection('asc');
                                                                    }
                                                                }}
                                                            >
                                                                Output Cost {sortColumn === 'output' && (sortDirection === 'asc' ? '▲' : '▼')}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(selectedProvider === "OpenAI" ? openAiModels : selectedProvider === "Google" ? googleModels : openRouterModels)
                                                            .map(m => {
                                                                // Get model pricing and costs
                                                                const modelName = m.value;

                                                                const p = resolveModelPricing(pricing, FALLBACK_PRICING, selectedProvider, modelName) as ModelPricing | null;

                                                                const inputCost = p?.input_cost_per_1k_tokens ?? (p?.input_cost_per_token != null ? p.input_cost_per_token * 1000 : null);
                                                                const outputCost = p?.output_cost_per_1k_tokens ?? (p?.output_cost_per_token != null ? p.output_cost_per_token * 1000 : null);

                                                                return {
                                                                    model: m,
                                                                    inputCost,
                                                                    outputCost,
                                                                    label: m.label
                                                                };
                                                            })
                                                            // Sort the array based on current sort column and direction
                                                            .sort((a, b) => {
                                                                let comparison = 0;
                                                                if (sortColumn === 'model') {
                                                                    comparison = a.label.localeCompare(b.label);
                                                                } else if (sortColumn === 'input') {
                                                                    // Handle null values and sort numerically
                                                                    if (a.inputCost === null && b.inputCost === null) comparison = 0;
                                                                    else if (a.inputCost === null) comparison = 1;
                                                                    else if (b.inputCost === null) comparison = -1;
                                                                    else comparison = a.inputCost - b.inputCost;
                                                                } else if (sortColumn === 'output') {
                                                                    // Handle null values and sort numerically
                                                                    if (a.outputCost === null && b.outputCost === null) comparison = 0;
                                                                    else if (a.outputCost === null) comparison = 1;
                                                                    else if (b.outputCost === null) comparison = -1;
                                                                    else comparison = a.outputCost - b.outputCost;
                                                                }
                                                                return sortDirection === 'asc' ? comparison : -comparison;
                                                            })
                                                            .map(({ model: m, inputCost, outputCost }) => {
                                                                // Format costs
                                                                const formatCost = (cost: number | null) => {
                                                                    if (cost === null) return "N/A";
                                                                    if (cost === 0) return "Free";
                                                                    // Always use fixed decimals to avoid scientific notation.
                                                                    // Use more decimals for very small values.
                                                                    const decimals = cost < 0.001 ? 7 : 4;
                                                                    const fixed = cost.toFixed(decimals);
                                                                    // Trim trailing zeros but keep at least 4 decimals
                                                                    let [intPart, fracPart = ""] = fixed.split(".");
                                                                    fracPart = fracPart.replace(/0+$/, "");
                                                                    if (fracPart.length < 4) {
                                                                        fracPart = fracPart.padEnd(4, "0");
                                                                    }
                                                                    const display = `${intPart}.${fracPart}`;
                                                                    return `$${display}/1K`;
                                                                };

                                                                return (
                                                                    <tr
                                                                        key={m.value}
                                                                        onClick={() => {
                                                                            setValue('model', m.value, { shouldDirty: true, shouldTouch: true })
                                                                        }}
                                                                        className={m.value === selectedModel ? 'selected' : ''}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        <td>{m.label}</td>
                                                                        <td>{formatCost(inputCost)}</td>
                                                                        <td>{formatCost(outputCost)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )
                                    ) : (
                                        <div className="text-muted">
                                            {!selectedProvider || selectedProvider === ''
                                                ? 'First select a provider to see available models'
                                                : 'No model selection needed for None provider'}
                                        </div>
                                    )}
                                    <small className="text-danger">{errors.model?.message}</small>
                                    <input type="hidden" {...register('model', {
                                        required: selectedProvider && selectedProvider !== 'None' && selectedProvider !== 'AzureOpenAI' ? "Please select a model" : false
                                    })} />
                                </div>
                            </div>
                            <div className="w-100" />


                            {/* API Key */}
                            <div className="col-xs-12 col-md-3">
                                <span className="required"><label htmlFor="apikey" className="reminder" data-tip="Set the API key">API Key</label></span>
                                <i className="fas fa-eye fa-fw fa-xs ms-1" role="button"
                                    onClick={() => ((apikeyElementType === 'text') ? setApiKeyElementType('password') : setApiKeyElementType('text'))}></i>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-4" style={{ width: '500px' }}>

                                <input
                                    type={apikeyElementType}
                                    id="url"
                                    maxLength={250}
                                    {...register("plainkey", { required: "Please fill in this field" })}
                                    className={`form-control ${getValidClass(errors.plainkey, getValues('plainkey'), isDelete)}`}
                                    defaultValue=''
                                    disabled={disabled || validatingApiKey}
                                    title={errors.plainkey?.message}
                                    data-lpignore={true}
                                />
                                <small className="text-danger">{errors.plainkey?.message}</small>
                                {apiKeyError && (
                                    <div className="text-danger mt-2">
                                        <i className="fas fa-exclamation-triangle me-1"></i>
                                        {apiKeyError}
                                    </div>
                                )}
                                {validatingApiKey && (
                                    <div className="text-info mt-2">
                                        <i className="fas fa-spinner fa-spin me-1"></i>
                                        Validating API key...
                                    </div>
                                )}
                            </div>
                            <div className="w-100"></div>

                            {/* Enabled */}
                            <div className="col-xs-12 col-md-3">
                                <label className="auth">Enabled</label>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-3">
                                <div className='form-check form-switch'>
                                    <input
                                        type="checkbox"
                                        id="enable"
                                        {...register("enable")}
                                        disabled={disabled}
                                        className="form-check-input"
                                        // onChange={() => setValue('enable', !getValues('enable'))}
                                        defaultChecked={true}
                                        data-lpignore={true}
                                    />
                                </div>
                            </div>
                            <div className="w-100" />


                            {/* Actions */}
                            <div className="col-12 actions">
                                <Link to="/admin/integrations/co-pilot" role="button" className="btn btn-secondary"><i
                                    className="fal fa-times-circle fa-fw fa-sm" /><span>Cancel</span></Link>
                                <button type="submit" className={`btn btn-${action.variant}`} disabled={(!isDelete && disabled) || validatingApiKey}>
                                    <i className={`${validatingApiKey ? 'fas fa-spinner fa-spin' : action.icon} fa-sm`} />
                                    <span>{validatingApiKey ? 'Validating...' : action.title}</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

Detail.propTypes = {
    coPilot: PropTypes.array.isRequired,
    refresh: PropTypes.func.isRequired,
};

export default Detail;